import { useState } from 'react'

interface DataTableSortingProps<Column> {
  columns: Column[]
  singleSort?: boolean
}

type SortParam = 'asc' | 'desc' | null
type SortValue = string | null

export interface TableSorting<Column> {
  sort: SortValue
  control: {
    columns: Column[]
    sortingColumns: {
      column: Column
      sort: SortParam
    }[]
    setSort(column: Column, sort: SortParam): void
    getColumnSortValue(column: Column): SortParam
  }
}

export function useDataTableSorting<Column>(
  props: DataTableSortingProps<Column>
): TableSorting<Column> {
  const { singleSort = true } = props
  const [addedSortingColumns, setAddedSortingColumns] = useState<
    TableSorting<Column>['control']['sortingColumns']
  >([])

  const setSort = (column: Column, sort: SortParam) => {
    let addedSorts = [...addedSortingColumns]
    const existingIndex = addedSorts?.findIndex((item) => item?.column === column)

    if (existingIndex > -1) {
      addedSorts[existingIndex].sort = sort
    } else {
      if (singleSort) {
        addedSorts = [
          {
            column,
            sort,
          },
        ]
      } else {
        addedSorts.push({
          column,
          sort,
        })
      }
    }

    setAddedSortingColumns(() => addedSorts)
  }

  const getSorting = (): SortValue => {
    let sort = ''
    let index = 0

    addedSortingColumns
      ?.filter((i) => i?.sort !== null)
      ?.forEach((item) => {
        sort += `${index > 0 ? ',' : ''}${item?.sort === 'desc' ? '-' : ''}${item?.column}`

        index++
      })

    if (addedSortingColumns?.length > 0 && sort === '') {
      return null
    } else if (sort === '') {
      return null
    }

    return sort
  }

  const sort = getSorting()

  const getColumnSortValue = (column: Column): SortParam => {
    return addedSortingColumns?.find((i) => i?.column === column)?.sort ?? null
  }

  return {
    sort,
    control: {
      columns: props?.columns,
      sortingColumns: addedSortingColumns,
      setSort,
      getColumnSortValue,
    },
  }
}
